/**
 * „Materiały" — the links a trainer's clients see under this movement, read and edited in place.
 *
 * Two modes in one box. READ mode is the canon's own drawing: the rows with their brand marks, the
 * „n z 3" count, the sentence that says whose links these are, and the two text buttons. EDIT mode
 * replaces the rows with fields and adds a footer with „Zapisz materiały" and „Anuluj". Everything
 * either mode decides is `model/materialsEditor`'s and `model/materialsStatus`'s; this file draws.
 *
 * ── The brand mark is chosen from the HOST the server resolved ──────────────────────────────
 * `brandKeyOf(displayHost)` — never from the raw address, and never guessed from the label. That is
 * the spelling the server says a human should read and the one printed under the label, so the mark
 * and the words beneath it can never name two different places. `./LinkBrandMarks` draws it,
 * on a light plate in both themes: a multi-colour logo on a near-black ground loses its dark strokes.
 *
 * ── The sentence and the footer are RESERVED, because the overlay read lands late ────────────
 * The rows are the catalogue's and arrive with everything else. Which of the three sentences applies,
 * and whether „Przywróć wersję z biblioteki" is offered at all, come from `GET /exercise/{id}/overlay`.
 * Both boxes hold their height from the first frame, so its answer changes ink and never geometry.
 *
 * ── The refusal stands under the field that caused it ───────────────────────────────────────
 * One row at a time, because the policy stops at the first — which is also what the server does, so
 * a list this editor accepts and a list it refuses are the same two lists on both sides. The
 * sentence is the phone's own, and it is wired as the field's
 * description rather than only painted beside it.
 *
 * ── One live region for the SCREEN's status, one alert for this block's failures ────────────
 * The list's `role="status"` describes the table and this block adds no second one: a save's
 * outcome is visible where it happened — the editor closes and the rows change — and two polite
 * regions describing one surface is how a listener ends up hearing neither. What this block DOES
 * carry is a `role="alert"` for a refused write, which is a different channel and the right one: a
 * save that failed is not a status the trainer will notice by looking.
 *
 * ── A refused save SAYS why, and hands the trainer the field ────────────────────────────────
 * „Zapisz materiały" is `aria-disabled` rather than `disabled`, so it stays in the tab order and
 * the reason under it is reachable (view ADR §12) — but it had no reason to point at, so a press
 * did nothing, said nothing and left focus where it was. `materialsSaveBlock` names which of the
 * two it is; the foot prints it and the button is described by it; and a press that is refused
 * because a row is refused puts the caret in THAT row's address field.
 *
 * Both that sentence and the save failure's `role="alert"` hold their line from the first frame.
 * The alert used to be inserted when a write failed, which pushed the whole footer down inside a
 * rail the trainer was reading — and an alert region inserted together with its text is one some
 * screen readers do not announce at all.
 *
 * ── Reverting asks first ────────────────────────────────────────────────────────────────────
 * `DELETE /exercise/{id}/overlay` throws the trainer's version away and there is no undo — the
 * phone's own sentence says as much. `shared/ui/ConfirmDialog` is the app's one prompt for that.
 */

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MAX_EXERCISE_LINKS, brandKeyOf, type ExerciseLink } from '~/entities/exercise-library';
import { LinkBrandMark } from '~/entities/exercise-library';
import { linkRefusalKey } from '~/entities/exercise-library';
import { ConfirmDialog } from '~/shared/ui/ConfirmDialog';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineText from '~/shared/ui/inlineTextButton.module.css';

import type { ExerciseMaterialsState } from '../lib/useExerciseMaterials';
import {
  materialsSaveBlock,
  type MaterialRowView,
} from '../model/materialsEditor';

import { TrashGlyph } from '~/shared/ui/FormGlyphs';
import { ExternalGlyph } from './ExerciseLibraryIcons';
import styles from './MaterialsBlock.module.css';

/** One saved material, as read mode draws it: a mark, the label over its host, and the way out. */
function LinkRow({ link }: { readonly link: ExerciseLink }) {
  const { t } = useTranslation();
  // The trainer gave no words for it, so the address stands for itself. Deciding that is the ROW's
  // and not the projection's — see `entities/exercise-library`'s `toLinks`.
  const label = link.label === '' ? link.url : link.label;

  return (
    <a
      className={styles.row}
      href={link.url}
      target="_blank"
      rel="noreferrer noopener"
      aria-label={t('exerciseLibrary.links.openA11y', { name: label, host: link.displayHost })}
    >
      <span className={styles.mark}>
        <LinkBrandMark brand={brandKeyOf(link.displayHost)} className={styles.markGlyph} />
      </span>
      <span className={styles.rowText}>
        <span className={styles.rowLabel}>{label}</span>
        <span className={styles.rowHost}>{link.displayHost}</span>
      </span>
      <ExternalGlyph className={styles.rowOut} />
    </a>
  );
}

/** One row of the editor: an address, a name for it, and the way to take it out. */
function EditorRow({
  row,
  position,
  busy,
  onUrl,
  onLabel,
  onRemove,
}: {
  readonly row: MaterialRowView;
  readonly position: number;
  readonly busy: boolean;
  readonly onUrl: (value: string) => void;
  readonly onLabel: (value: string) => void;
  readonly onRemove: () => void;
}) {
  const { t } = useTranslation();
  const refusalId = `${row.draft.key}-refusal`;
  const refused = row.refusal !== null;

  return (
    <div className={styles.editorRow}>
      <div className={styles.fields}>
        <input
          className={refused ? `${String(styles.field)} ${String(styles.fieldRefused)}` : styles.field}
          type="url"
          inputMode="url"
          value={row.draft.url}
          placeholder={t('exerciseLibrary.links.urlPlaceholder')}
          aria-label={t('exerciseLibrary.links.urlA11y', { position })}
          aria-invalid={refused}
          aria-describedby={refused ? refusalId : undefined}
          disabled={busy}
          onChange={(event) => {
            onUrl(event.target.value);
          }}
        />
        <input
          className={styles.field}
          type="text"
          value={row.draft.label}
          placeholder={t('exerciseLibrary.links.labelPlaceholder')}
          aria-label={t('exerciseLibrary.links.labelA11y', { position })}
          disabled={busy}
          onChange={(event) => {
            onLabel(event.target.value);
          }}
        />
      </div>
      <button
        type="button"
        className={styles.remove}
        aria-label={t('exerciseLibrary.links.removeA11y', { position })}
        disabled={busy}
        onClick={onRemove}
      >
        <TrashGlyph className={styles.removeGlyph} />
      </button>
      {/* Read as the field's description AND printed under it. A refusal only a screen reader can
        * reach is a refusal a sighted trainer meets as a button that does nothing. */}
      {row.refusal !== null && (
        <p className={styles.refusal} id={refusalId}>
          {t(linkRefusalKey(row.refusal, row.draft.url))}
        </p>
      )}
    </div>
  );
}

export function MaterialsBlock({ materials }: { readonly materials: ExerciseMaterialsState }) {
  const { t } = useTranslation();
  const [confirmingRevert, setConfirmingRevert] = useState(false);
  /* Where the revert prompt hands focus back. „Przywróć wersję z biblioteki" is gone from the DOM
   * the moment the overlay is dropped — that is the whole point of pressing it — so Base UI's own
   * return target would be a detached node and focus would land on `<body>`. The block's heading is
   * the honest destination: it names the section that just changed. */
  const heading = useRef<HTMLHeadingElement>(null);

  const { status, links, editor, busy } = materials;
  const count = editor === null ? links.length : editor.rows.length;
  // ONCE, and read by the control's state, its description and its handler alike. Written twice,
  // the three drift the first time one of the conditions changes — and then the button refuses a
  // press it draws as available, or takes one it draws as refused.
  const block = editor === null ? null : materialsSaveBlock(editor);
  const canSave = editor !== null && !busy && block === null;
  const noteId = useId();

  /* The caret follows the row that just appeared.
   *
   * It runs on entering the editor and on every add and remove, and on nothing else — a keystroke
   * does not change the count. Entering puts the trainer in the field they came to fill; „Dodaj
   * link" puts them in the new one; and a REMOVE has to move focus somewhere, because the button
   * that had it has just been unmounted and focus would otherwise land on `<body>`. */
  const rowCount = editor === null ? null : editor.rows.length;
  const rowsBox = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (rowCount === null) return;
    const fields = rowsBox.current?.querySelectorAll<HTMLInputElement>('input[type="url"]');
    fields?.[fields.length - 1]?.focus();
  }, [rowCount]);

  return (
    <section className={styles.block}>
      <div className={styles.head}>
        <h3 className={styles.title} ref={heading} tabIndex={-1}>
          {t('web:exerciseLibrary.theatre.sections.materials')}
        </h3>
        <span className={styles.count}>
          {t('web:exerciseLibrary.materials.count', { count, max: MAX_EXERCISE_LINKS })}
        </span>
      </div>

      {/* Reserved whether or not the overlay read has answered — see the header. With no links
        * there is no note in any state (`model/materialsStatus`), and the count is the catalogue's,
        * so the line is not reserved at all. */}
      {links.length > 0 && (
        <p className={styles.note}>{status.noteKey === null ? '' : t(status.noteKey)}</p>
      )}

      {editor === null ? (
        <div className={styles.rows}>
          {links.length === 0 ? (
            <p className={styles.empty}>{t('web:exerciseLibrary.materials.empty')}</p>
          ) : (
            // The address plus its position: the wire does not de-duplicate, and two identical
            // urls would otherwise share a key.
            links.map((link, index) => <LinkRow key={`${link.url}-${String(index)}`} link={link} />)
          )}
        </div>
      ) : (
        <div className={styles.rows} ref={rowsBox}>
          {editor.rows.map((row, index) => (
            <EditorRow
              key={row.draft.key}
              row={row}
              position={index + 1}
              busy={busy}
              onUrl={(value) => {
                materials.dispatch({ type: 'setUrl', key: row.draft.key, value });
              }}
              onLabel={(value) => {
                materials.dispatch({ type: 'setLabel', key: row.draft.key, value });
              }}
              onRemove={() => {
                materials.dispatch({ type: 'remove', key: row.draft.key });
              }}
            />
          ))}
        </div>
      )}

      {/* RESERVED and always mounted, for both of the reasons in the header: the line is held so a
        * failed write changes ink rather than geometry, and a `role="alert"` inserted together
        * with its text is a region that did not exist when the thing happened. */}
      <p className={styles.saveError} role="alert">
        {materials.refusalKey === null ? '' : t(materials.refusalKey)}
      </p>

      {/* The footer's row is always drawn and always the same height, so a control arriving with the
        * overlay reading pushes nothing under it. */}
      <div className={styles.foot}>
        {editor === null ? (
          <>
            {status.canAdd && (
              <button
                type="button"
                className={inlineText.inlineTextButton}
                disabled={busy}
                onClick={() => {
                  materials.edit(true);
                }}
              >
                {t('exerciseLibrary.links.addCta')}
              </button>
            )}
            {links.length > 0 && (
              <button
                type="button"
                className={inlineText.inlineTextButton}
                disabled={busy}
                onClick={() => {
                  materials.edit(false);
                }}
              >
                {t('web:exerciseLibrary.materials.edit')}
              </button>
            )}
            {status.canRevert && (
              <button
                type="button"
                className={inlineText.inlineTextButton}
                disabled={busy}
                onClick={() => {
                  setConfirmingRevert(true);
                }}
              >
                {t('exerciseLibrary.links.revertCta')}
              </button>
            )}
          </>
        ) : (
          <>
            {/* The button's description, and the foot's own reserve: one line, always there, empty
              * while the save may act. See the header. */}
            <p className={styles.saveNote} id={noteId}>
              {block === null
                ? ''
                : t(
                    block === 'refused'
                      ? 'web:exerciseLibrary.materials.fixAddressFirst'
                      : 'web:exerciseLibrary.materials.nothingToSave',
                  )}
            </p>
            {editor.canAdd && (
              <button
                type="button"
                className={inlineText.inlineTextButton}
                disabled={busy}
                onClick={() => {
                  materials.dispatch({ type: 'add' });
                }}
              >
                {t('exerciseLibrary.links.addCta')}
              </button>
            )}
            <span className={styles.spacer} />
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(styles.footButton)}`}
              disabled={busy}
              onClick={materials.cancel}
            >
              {t('common.cancel')}
            </button>
            {/* `aria-disabled`, not the attribute: a control that refuses has to stay in the tab
              * order for the reason under the field to be reachable at all (view ADR §12). */}
            <button
              type="button"
              className={`${String(ghost.ghost)} ${String(styles.footButton)} ${String(styles.footSave)}`}
              aria-disabled={!canSave}
              aria-describedby={block === null ? undefined : noteId}
              onClick={() => {
                if (canSave) {
                  materials.save();
                  return;
                }
                // A refused press is an ACT, not a no-op: the sentence under the button says what
                // is wrong and this puts the trainer in the field that is wrong. „Nothing to save"
                // has no field to hand over, so it says its sentence and stays put.
                if (block !== 'refused') return;
                const at = editor.rows.findIndex((row) => row.refusal !== null);
                const fields = rowsBox.current?.querySelectorAll<HTMLInputElement>(
                  'input[type="url"]',
                );
                if (at >= 0) fields?.[at]?.focus();
              }}
            >
              {t('exerciseLibrary.links.saveCta')}
            </button>
          </>
        )}
      </div>

      <ConfirmDialog
        prompt={
          confirmingRevert
            ? {
                title: t('exerciseLibrary.links.revertConfirmTitle'),
                description: t('exerciseLibrary.links.revertConfirmBody'),
                confirmLabel: t('exerciseLibrary.links.revertConfirmCta'),
                cancelLabel: t('common.cancel'),
                destructiveConfirm: true,
                busy,
              }
            : null
        }
        onConfirm={() => {
          setConfirmingRevert(false);
          materials.revert();
        }}
        onCancel={() => {
          setConfirmingRevert(false);
        }}
        finalFocus={heading}
      />
    </section>
  );
}
